import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import RiskStratificationService from "../service/RiskStratificationService";
import ChiefComplaintsService from "../sections/chiefComplaints/ChiefComplaintsService";
import DiagnosisService from "../sections/diagnosis/DiagnosisService";
import HospitalisationService from "../sections/hospitalisation/HospitalisationService";
import ActivityService from "../sections/physicalActivity/ActivityService";
import { CC_V2_REFRESH } from "../sections/chiefComplaints/events";
import { DX_V2_REFRESH } from "../sections/diagnosis/events";
import { HOSP_V2_REFRESH } from "../sections/hospitalisation/events";
import { ACT_V2_REFRESH } from "../sections/physicalActivity/events";
import AuthenticationService from "service/auth/AuthenticationService";

import "../styles/patientOverview.scss";

/**
 * Patient health overview — the left card of the overview row. Source: Figma
 * node 2428:4821 "ALT B · Patient health overview (ghost chips)", read via Dev
 * Mode MCP. Card is 10px radius / 20px-16px padding, not the 12px $radius the
 * section cards use — that's the board's value, not a slip.
 *
 * The risk pill + narrative are LIVE from risk stratification (latest
 * assessment via history → detail; the narrative is detail-only). Everything
 * else — health score, quick glimpse, "Re-calculate risk" — is still sample
 * content awaiting its own source. The chips are the board's "ghost" variant
 * (h38 / fully-round / no affordance) — deliberately not the qp-chip in
 * sections.scss, which is the h28/radius-8 add-chip with a + glyph.
 */

/** Bridge's level enum, in this board's sentence case. 1 is worst. */
const RISK_LEVEL_LABELS = {
  1: "Life threatening risk",
  2: "Very high risk",
  3: "High risk",
  4: "Moderate risk",
  5: "Low risk",
};

const RISK_LEVEL_CLASSES = {
  1: "is-life-threatening",
  2: "is-very-high",
  3: "is-high",
  4: "is-moderate",
  5: "is-low",
};

/**
 * Sample content for the still-unwired parts of the card. Only `healthScore`
 * remains sample data — glimpse groups are now populated from the four
 * clinical v2 services (chief complaints, diagnosis, hospitalisation,
 * physical activity) via `useGlimpse` below.
 */
export const HEALTH_OVERVIEW_SAMPLE = {
  healthScore: 82,
};

/**
 * Extract a display string for a hospitalisation's cause. Prefers the ICD
 * name (denormalised from api-icd-service) because it reads more
 * naturally than the code; falls back to the free-text description.
 * Empty when nothing usable — caller can filter such rows out.
 */
const causeChip = (h) => {
  const c = (h && h.cause) || {};
  return String(c.icdName || c.description || "").trim();
};

/**
 * Pull the four v2 sections in parallel and derive the glimpse groups.
 * Loading is best-effort: each source is independent, so a section
 * whose service is slow or dead doesn't block the rest. Empty groups
 * are dropped so the render only shows sections that actually have
 * active entries.
 */
const useGlimpse = (patientId) => {
  const cc   = useRef(new ChiefComplaintsService()).current;
  const dx   = useRef(new DiagnosisService()).current;
  const hosp = useRef(new HospitalisationService()).current;
  const act  = useRef(new ActivityService()).current;

  const [state, setState] = useState({
    loading: true,
    complaints: [],
    diagnoses: [],
    hospitalisations: [],
    activities: [],
  });

  // Monotonic request token — bumped on every fetch; each response
  // callback compares its captured token to the current one and
  // silently drops when they differ. Cheaper than a per-call
  // AbortController for a hook that fires four HTTP calls per refresh.
  const seqRef = useRef(0);
  const loadGlimpse = useCallback(() => {
    if (!patientId) {
      setState({ loading: false, complaints: [], diagnoses: [],
                 hospitalisations: [], activities: [] });
      return;
    }
    const mySeq = ++seqRef.current;
    setState((prev) => ({ ...prev, loading: true }));
    let pending = 4;
    const bump = (patch) => {
      if (mySeq !== seqRef.current) return; // superseded — drop.
      setState((prev) => {
        const next = { ...prev, ...patch };
        pending -= 1;
        if (pending <= 0) next.loading = false;
        return next;
      });
    };

    // CC v2 already splits active/past server-side.
    cc.list(patientId,
      (doc) => bump({ complaints: (doc && doc.active) || [] }),
      ()    => bump({ complaints: [] }),
    );
    // Dx v2 too. Diagnosis names live on `.diagnosis`.
    dx.list(patientId,
      (doc) => bump({ diagnoses: (doc && doc.active) || [] }),
      ()    => bump({ diagnoses: [] }),
    );
    // Hospitalisation v2 returns a flat list — filter to status
    // "hospitalised" (the two-state enum: hospitalised | discharged).
    hosp.list(patientId,
      (doc) => bump({
        hospitalisations: ((doc && doc.hospitalisations) || [])
          .filter((h) => String(h && h.status || "").toLowerCase() === "hospitalised"),
      }),
      () => bump({ hospitalisations: [] }),
    );
    // Physical activity v2 also flat — surface anything the patient
    // is still doing at all. Status enum is active / occasional /
    // inactive; the first two mean "part of current routine" and
    // belong on the glimpse. Only "inactive" (fully stopped) is
    // hidden.
    act.list(patientId,
      (doc) => bump({
        activities: ((doc && doc.activities) || [])
          .filter((a) => {
            const s = String(a && a.status || "").toLowerCase();
            return s === "active" || s === "occasional";
          }),
      }),
      () => bump({ activities: [] }),
    );
  }, [patientId, cc, dx, hosp, act]);

  useEffect(() => {
    loadGlimpse();
    // Subscribe to each section's refresh broadcast so an add/update
    // in Chief complaints / Diagnosis / Hospitalisation / Physical
    // activity is reflected on the dashboard card without needing a
    // menu switch. Anthropometric + Lab investigation cards already
    // use this pattern (see AnthroOverviewCard.js — ANTH_V2_REFRESH /
    // LI_V2_REFRESH); this is the missing pair.
    EventService.addListener(CC_V2_REFRESH,   loadGlimpse);
    EventService.addListener(DX_V2_REFRESH,   loadGlimpse);
    EventService.addListener(HOSP_V2_REFRESH, loadGlimpse);
    EventService.addListener(ACT_V2_REFRESH,  loadGlimpse);
    return () => {
      // Bumping the seq invalidates any in-flight responses so a late
      // callback doesn't setState after unmount.
      seqRef.current += 1;
      EventService.removeListener(CC_V2_REFRESH,   loadGlimpse);
      EventService.removeListener(DX_V2_REFRESH,   loadGlimpse);
      EventService.removeListener(HOSP_V2_REFRESH, loadGlimpse);
      EventService.removeListener(ACT_V2_REFRESH,  loadGlimpse);
    };
  }, [loadGlimpse]);

  // Derive the ordered list of glimpse groups. Group order matches the
  // mockup: hospitalisation highlight (unlabelled) → complaints → dx →
  // activity. An empty group is omitted entirely; the render should
  // never draw a labelled section with no chips inside.
  return useMemo(() => {
    if (state.loading) return { loading: true, groups: [] };
    const groups = [];
    // Hospitalisation highlight — one chip per active row, prefixed
    // "Hospitalised-" per the mockup.
    const hospChips = state.hospitalisations
      .map((h) => {
        const name = causeChip(h);
        return name ? `Hospitalised- ${name}` : "";
      })
      .filter(Boolean);
    if (hospChips.length) {
      groups.push({ id: "highlight", label: null, items: hospChips });
    }
    // Chief complaints.
    const ccChips = state.complaints
      .map((c) => String(c && c.complaint || "").trim())
      .filter(Boolean);
    if (ccChips.length) {
      groups.push({ id: "complaints", label: "Chief complaints", items: ccChips });
    }
    // Diagnosis.
    const dxChips = state.diagnoses
      .map((d) => String(d && d.diagnosis || "").trim())
      .filter(Boolean);
    if (dxChips.length) {
      groups.push({ id: "diagnosis", label: "Diagnosis", items: dxChips });
    }
    // Physical activity.
    const actChips = state.activities
      .map((a) => String(a && a.name || "").trim())
      .filter(Boolean);
    if (actChips.length) {
      groups.push({ id: "activity", label: "Physical activity", items: actChips });
    }
    return { loading: false, groups };
  }, [state]);
};

/** The refresh glyph on the "Re-calculate risk" button (icon/refresh, 13×13). */
const RefreshIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 13 13" fill="none" aria-hidden="true">
    <path
      d="M11.4 6.2C11.4 9.07 9.07 11.4 6.2 11.4C3.33 11.4 1 9.07 1 6.2C1 3.33 3.33 1 6.2 1C8.05 1 9.68 1.97 10.6 3.42"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <path d="M8.4 0.6H12.8V5L8.4 0.6Z" fill="currentColor" />
  </svg>
);
RefreshIcon.propTypes = { className: PropTypes.string };

/** Material Symbols Rounded `vital_signs` (400, unfilled), on that set's 960 grid. */
const VitalSignsIcon = () => (
  <svg viewBox="0 -960 960 960" fill="currentColor" aria-hidden="true">
    <path d="M326-171q-15-11-22-28l-92-241H80q-17 0-28.5-11.5T40-480q0-17 11.5-28.5T80-520h160q13 0 22.5 7t14.5 19l83 218 184-485q7-17 22-28t34-11q19 0 34 11t22 28l92 241h132q17 0 28.5 11.5T920-480q0 17-11.5 28.5T880-440H720q-13 0-22.5-7T683-466l-83-218-184 485q-7 17-22 28t-34 11q-19 0-34-11Z" />
  </svg>
);

/**
 * "No health data yet" — the risk block when the patient has no assessment.
 * Above a quick glimpse it is Figma 6107:13507; `whole` is the card with
 * nothing else in it either (6107:15013), where it centres in the card.
 */
const NoHealthData = ({ whole }) => (
  <div className={`po-empty${whole ? " is-whole" : ""}`}>
    <span className="po-empty-art" aria-hidden="true">
      <span className="po-empty-bar is-left" />
      <span className="po-empty-bar is-right" />
      <span className="po-empty-disc">
        <VitalSignsIcon />
      </span>
    </span>
    <p className="po-empty-copy">No health data yet</p>
  </div>
);
NoHealthData.propTypes = { whole: PropTypes.bool };

const HealthOverviewCard = ({ patient }) => {
  const named = [patient && patient.firstName, patient && patient.lastName]
    .filter(Boolean)
    .join(" ");
  const data = HEALTH_OVERVIEW_SAMPLE;
  const patientId = patient && patient.id;
  const service = useRef(new RiskStratificationService()).current;
  const glimpse = useGlimpse(patientId);

  // The latest risk assessment: history (newest-first) picks the record,
  // detail brings the level + AI narrative. `riskSummary` only exists on
  // detail, so both calls are unavoidable.
  const [risk, setRisk] = useState({ state: "loading" });
  const [recalcing, setRecalcing] = useState(false);
  const [recalcError, setRecalcError] = useState("");

  /**
   * The logged-in doctor, which the stratify call is attributed to. An
   * employee-only login carries `doctorProfile.id` 0 (or none), and there is
   * nothing sensible to attribute an assessment to — so the button is disabled
   * rather than firing a request with a 0 in the path.
   */
  const docId = (() => {
    const p = new AuthenticationService().getUserProfile();
    const id = p && p.doctorProfile && p.doctorProfile.id;
    return id > 0 ? id : 0;
  })();

  const loadRisk = useCallback(() => {
    if (!patientId) {
      // No patient, no fetch — land on the honest note, not eternal loading.
      setRisk({ state: "empty" });
      return undefined;
    }
    let cancelled = false;
    setRisk({ state: "loading" });
    service.getHistory(
      patientId,
      (rows) => {
        if (cancelled) return;
        const list = Array.isArray(rows) ? rows.filter((r) => r && r.id) : [];
        if (!list.length) {
          setRisk({ state: "empty" });
          return;
        }
        // The wire is newest-first today; sort anyway — the order is the
        // backend's, not a contract. NaN-safe: a row with a malformed
        // triggeredAt sorts oldest instead of poisoning the comparator.
        const ts = (r) => {
          const t = new Date(r.triggeredAt).getTime();
          return isNaN(t) ? 0 : t;
        };
        const newest = [...list].sort((a, b) => ts(b) - ts(a))[0];
        service.getDetail(
          newest.id,
          (detail) => {
            if (cancelled) return;
            // An evaluator's corrected level wins over the raw AI one.
            const level =
              (detail && (detail.updatedRiskLevel || detail.riskLevel)) || 0;
            if (!RISK_LEVEL_LABELS[level]) {
              setRisk({ state: "empty" });
              return;
            }
            setRisk({
              state: "loaded",
              level,
              summary: String((detail && detail.riskSummary) || "").trim(),
            });
          },
          () => !cancelled && setRisk({ state: "error" })
        );
      },
      () => !cancelled && setRisk({ state: "error" })
    );
    return () => {
      cancelled = true;
    };
  }, [patientId, service]);

  useEffect(() => {
    loadRisk();
    // Same four broadcasts drive the Patient-health-overview risk
    // block — a new complaint / diagnosis / hospitalisation /
    // activity is exactly the kind of change a risk assessment would
    // reflect, so re-read the latest whenever any of them fire. This
    // is a READ ONLY: no new stratify call, just a fresh
    // getHistory + getDetail.
    EventService.addListener(CC_V2_REFRESH,   loadRisk);
    EventService.addListener(DX_V2_REFRESH,   loadRisk);
    EventService.addListener(HOSP_V2_REFRESH, loadRisk);
    EventService.addListener(ACT_V2_REFRESH,  loadRisk);
    return () => {
      EventService.removeListener(CC_V2_REFRESH,   loadRisk);
      EventService.removeListener(DX_V2_REFRESH,   loadRisk);
      EventService.removeListener(HOSP_V2_REFRESH, loadRisk);
      EventService.removeListener(ACT_V2_REFRESH,  loadRisk);
    };
  }, [loadRisk]);

  /**
   * Trigger a fresh assessment, then re-read it.
   *
   * The stratify endpoint is a GET that MUTATES, so it is fired only from this
   * click — never from an effect, and never retried automatically. The response
   * body is not the new assessment, so the card re-reads history + detail
   * afterwards rather than trying to parse it.
   */
  const recalculate = () => {
    if (!patientId || !docId || recalcing) return;
    setRecalcing(true);
    setRecalcError("");
    service.stratify(
      patientId,
      docId,
      () => {
        setRecalcing(false);
        loadRisk();
      },
      (err) => {
        setRecalcing(false);
        // Say so on the card: a silent no-op is what this button already was.
        setRecalcError((err && err.message) || "Could not re-calculate risk.");
      }
    );
  };

  /* The empty states (Figma 6107:13507 / 6107:15013) are for a patient with
     NOTHING recorded: no assessment, and, for the whole card, no glimpse once
     it has loaded. Loading and a failed read keep their notes — neither is
     "no data". */
  const noRisk = risk.state === "empty";
  const wholeEmpty = noRisk && !glimpse.loading && glimpse.groups.length === 0;

  return (
    <section
      className="po-card"
      aria-label={named ? `Patient health overview for ${named}` : "Patient health overview"}
    >
      {/* The scroll owner. The card is a fixed height (_overviewCards.scss), so
          whatever runs past it scrolls in here, heading included — as in the
          Anthropometric card beside it. */}
      <div className="po-body">
        <header className="po-head">
          <h2 className="po-title">Patient health overview</h2>
          {/* Health score + Re-calculate risk are HIDDEN for this release
              (2026-08-26) \u2014 the backend scoring + stratify-on-demand
              flows aren't ready yet. The state, service, docId lookup,
              recalcError banner + all handlers below are intentionally
              kept so restoring the affordance is a JSX-only change once
              the backend lands. See git history for the previous JSX. */}
        </header>

        {noRisk ? (
          <NoHealthData whole={wholeEmpty} />
        ) : (
          <div className="po-risk-block">
            {risk.state === "loaded" ? (
              <>
                <span className={`po-risk-pill ${RISK_LEVEL_CLASSES[risk.level]}`}>
                  {RISK_LEVEL_LABELS[risk.level]}
                </span>
                {risk.summary ? <p className="po-summary">{risk.summary}</p> : null}
              </>
            ) : (
              <p className="po-risk-note">
                {risk.state === "loading"
                  ? "Loading risk assessment…"
                  : "Couldn't load the risk assessment."}
              </p>
            )}
          </div>
        )}

        {wholeEmpty ? null : (
          <div className="po-glimpse">
            <p className="po-glimpse-title">Quick glimpse</p>
            {glimpse.loading ? (
              <p className="po-glimpse-note">Loading glimpse…</p>
            ) : glimpse.groups.length === 0 ? (
              <p className="po-glimpse-note">
                No active complaints, diagnoses, hospitalisations or activities
                recorded yet.
              </p>
            ) : (
              glimpse.groups.map((group) => (
                <React.Fragment key={group.id}>
                  {group.label ? <p className="po-group-label">{group.label}</p> : null}
                  <div className="po-chips">
                    {group.items.map((item, i) => (
                      // key includes index — same chip text can legitimately
                      // repeat across two active hospitalisations (e.g. two
                      // "Hospitalised- Fever" rows for two admissions).
                      <span className="po-chip" key={`${group.id}-${i}-${item}`}>
                        {item}
                      </span>
                    ))}
                  </div>
                </React.Fragment>
              ))
            )}
          </div>
        )}
      </div>
    </section>
  );
};

HealthOverviewCard.propTypes = {
  /**
   * Optional, but the risk block needs `patient.id` — without it the card
   * shows its "No health data yet" state.
   */
  patient: PropTypes.object,
};

export default HealthOverviewCard;
